import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useAuth } from "./auth";

export type ThemeMode = "light" | "dark";
export type ColorScheme = "default" | "ocean" | "forest" | "sunset" | "lavender";

interface ThemeContextType {
  mode: ThemeMode;
  colorScheme: ColorScheme;
  setMode: (mode: ThemeMode) => void;
  setColorScheme: (scheme: ColorScheme) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile, updateProfile } = useAuth();
  const [mode, setModeState] = useState<ThemeMode>("light");
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>("default");

  // Refs to avoid stale closures in memoized callbacks
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const colorSchemeRef = useRef(colorScheme);
  colorSchemeRef.current = colorScheme;
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const updateProfileRef = useRef(updateProfile);
  updateProfileRef.current = updateProfile;

  useEffect(() => {
    if (profile?.theme) {
      try {
        const themeData = JSON.parse(profile.theme);
        if (themeData.mode) setModeState(themeData.mode);
        if (themeData.colorScheme) setColorSchemeState(themeData.colorScheme);
      } catch {
        const storedMode = localStorage.getItem("themeMode") as ThemeMode;
        const storedScheme = localStorage.getItem("colorScheme") as ColorScheme;
        if (storedMode) setModeState(storedMode);
        if (storedScheme) setColorSchemeState(storedScheme);
      }
    } else {
      const storedMode = localStorage.getItem("themeMode") as ThemeMode;
      const storedScheme = localStorage.getItem("colorScheme") as ColorScheme;
      if (storedMode) setModeState(storedMode);
      if (storedScheme) setColorSchemeState(storedScheme);
    }
  }, [profile?.theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    root.setAttribute("data-color-scheme", colorScheme);
  }, [mode, colorScheme]);

  const setMode = useCallback(async (newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem("themeMode", newMode);
    if (profileRef.current) {
      const themeData = JSON.stringify({ mode: newMode, colorScheme: colorSchemeRef.current });
      try {
        await updateProfileRef.current({ theme: themeData });
      } catch (error) {
        console.error("Failed to save theme to profile:", error);
      }
    }
  }, []);

  const setColorScheme = useCallback(async (newScheme: ColorScheme) => {
    setColorSchemeState(newScheme);
    localStorage.setItem("colorScheme", newScheme);
    if (profileRef.current) {
      const themeData = JSON.stringify({ mode: modeRef.current, colorScheme: newScheme });
      try {
        await updateProfileRef.current({ theme: themeData });
      } catch (error) {
        console.error("Failed to save theme to profile:", error);
      }
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(modeRef.current === "light" ? "dark" : "light");
  }, [setMode]);

  const value = useMemo(
    () => ({ mode, colorScheme, setMode, setColorScheme, toggleMode }),
    [mode, colorScheme, setMode, setColorScheme, toggleMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
